import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Search, Sparkles } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useSearch, useSearchHistory, type SearchResultItem } from '@/hooks/useSearch';
import { TIPO_LABELS } from '@/components/ItemStatusBadge';
import { cn } from '@/lib/utils';

const TIPO_ORDER = [
  'app-proprio',
  'app-aluno',
  'agente-autonomo',
  'agente-loop',
  'dominio',
  'mentorada',
  'skill',
  'doc',
  'curso',
  'aula',
  'projeto',
  'termo-glossario',
  'lousa',
];

interface SearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchPalette({ open, onOpenChange }: SearchPaletteProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isLoading, isError } = useSearch(debouncedQuery);
  const { data: historyData } = useSearchHistory();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setCursor(0);
    } else {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [debouncedQuery]);

  const flatResults: SearchResultItem[] = useMemo(() => {
    if (!data) return [];
    const ordered: SearchResultItem[] = [];
    const gruposOrdenados = TIPO_ORDER.filter((t) => data.grupos[t]?.length);
    for (const tipo of gruposOrdenados) {
      for (const r of data.grupos[tipo] ?? []) ordered.push(r);
    }
    // Tipos fora da ordem conhecida
    for (const [tipo, results] of Object.entries(data.grupos)) {
      if (!TIPO_ORDER.includes(tipo)) for (const r of results) ordered.push(r);
    }
    return ordered;
  }, [data]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (!open) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(flatResults.length - 1, c + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const r = flatResults[cursor];
        if (r) {
          navigate(`/items/${r.item.id}`);
          onOpenChange(false);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flatResults, cursor, navigate, onOpenChange]);

  const gruposOrdenados = data
    ? TIPO_ORDER.filter((t) => data.grupos[t]?.length)
    : [];

  let runningIdx = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar na Central…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
          <kbd className="text-[10px] bg-muted rounded px-1 py-0.5 font-mono">esc</kbd>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {debouncedQuery.length < 2 ? (
            <HistoryList
              historyEntries={historyData?.history ?? []}
              onSelect={(q) => setQuery(q)}
            />
          ) : isLoading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Buscando…</div>
          ) : isError ? (
            <div className="p-6 text-center text-sm text-destructive">
              Erro na busca. Tente de novo.
            </div>
          ) : !data || flatResults.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Nada encontrado pra "{debouncedQuery}". Tente outro termo ou verifique filtros.
            </div>
          ) : (
            <div>
              {gruposOrdenados.map((tipo) => {
                const results = data.grupos[tipo] ?? [];
                return (
                  <div key={tipo} className="py-1">
                    <div className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                      {TIPO_LABELS[tipo] ?? tipo} ({results.length})
                    </div>
                    {results.map((r) => {
                      const idx = runningIdx;
                      runningIdx += 1;
                      const active = idx === cursor;
                      return (
                        <button
                          type="button"
                          key={r.item.id}
                          onMouseEnter={() => setCursor(idx)}
                          onClick={() => {
                            navigate(`/items/${r.item.id}`);
                            onOpenChange(false);
                          }}
                          className={cn(
                            'w-full text-left px-3 py-2 flex flex-col gap-0.5 transition-colors',
                            active ? 'bg-accent' : 'hover:bg-accent/50',
                          )}
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium truncate flex-1">{r.item.nome}</span>
                            {r.semantic_score !== undefined && (
                              <span
                                title="Semantic score"
                                className="text-[10px] text-muted-foreground flex items-center gap-0.5"
                              >
                                <Sparkles className="h-3 w-3" />
                                {r.semantic_score.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {r.snippet}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
          <div className="flex gap-3">
            <span>
              <kbd className="bg-muted rounded px-1">↑↓</kbd> navegar
            </span>
            <span>
              <kbd className="bg-muted rounded px-1">↵</kbd> abrir
            </span>
            <span>
              <kbd className="bg-muted rounded px-1">esc</kbd> fechar
            </span>
          </div>
          {data && (
            <span>
              {data.total} result{data.total === 1 ? 'ado' : 'ados'} em {data.tempoMs}ms
              {data.usouSemantico ? ' · semântico' : ''}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryList({
  historyEntries,
  onSelect,
}: {
  historyEntries: Array<{ id: number; query: string }>;
  onSelect: (q: string) => void;
}): JSX.Element {
  if (historyEntries.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        Digite pelo menos 2 caracteres pra buscar.
      </div>
    );
  }
  return (
    <div className="py-2">
      <div className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Clock className="h-3 w-3" /> Recentes
      </div>
      {historyEntries.map((h) => (
        <button
          type="button"
          key={h.id}
          onClick={() => onSelect(h.query)}
          className="w-full text-left px-3 py-1.5 hover:bg-accent text-sm transition-colors"
        >
          {h.query}
        </button>
      ))}
    </div>
  );
}
